/**
 * Apply a user-preferred category order to the default category list.
 *
 * Rules:
 * 1. If preferredOrder is null/empty, return defaultCategories as-is.
 * 2. Preferred categories come first (in preferred order), but only if they
 *    exist in defaultCategories (unknown IDs are silently ignored).
 * 3. Any defaultCategories not in preferredOrder are appended at the end,
 *    preserving their original relative order.
 * 4. Duplicates in preferredOrder are ignored (first occurrence wins).
 */
export const applyCategoryOrder = (
  defaultCategories: string[],
  preferredOrder: string[] | null
): string[] => {
  if (!preferredOrder || preferredOrder.length === 0) {
    return defaultCategories;
  }

  const defaultSet = new Set(defaultCategories);

  // Deduplicate preferred order (first occurrence wins)
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const id of preferredOrder) {
    if (!seen.has(id)) {
      seen.add(id);
      deduped.push(id);
    }
  }

  // Preferred items that actually exist in the current defaults
  const ordered = deduped.filter((id) => defaultSet.has(id));

  // Remaining defaults not in the preferred order, preserving original order
  const orderedSet = new Set(ordered);
  const remaining = defaultCategories.filter((id) => !orderedSet.has(id));

  return [...ordered, ...remaining];
};
